import React, { useCallback } from 'react';
import { KeywordTokenTemplate } from '@/features/keyword-tokens/types';
import { setElementDragPoint } from '@/shared/utils/centerHtmlElementOnDrag';

const TOKEN_SIZE = 40;

interface TokenGridItemProps {
  template: KeywordTokenTemplate;
  size: number;
  /** Fluid: fill the (fractional) grid cell and stay square, capped at `size`.
   *  Lets a fixed column count shrink to fit narrow screens (the phone tray). */
  fluid?: boolean;
  onDragStart?: (template: KeywordTokenTemplate) => void;
  /** Tap-to-create (touch): clicking the item creates the token. When set, the
   *  item is also a real button so it's keyboard- and screen-reader-operable. */
  onSelect?: (template: KeywordTokenTemplate) => void;
}

function TokenGridItem({ template, size, fluid, onDragStart, onSelect }: TokenGridItemProps) {
  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setElementDragPoint(e.target as HTMLDivElement, e.nativeEvent, 'kwToken');
    e.dataTransfer.setData('text/x-keyword-token-template', JSON.stringify(template));
    e.dataTransfer.effectAllowed = 'copy';
    onDragStart?.(template);
  }, [template, onDragStart]);

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).style.opacity = '1';
  }, []);

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      // Tap-to-create surface (touch). onClick, not onSelect-on-drag, so the
      // desktop drag path is untouched — desktop callers pass no onSelect.
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? () => onSelect(template) : undefined}
      onKeyDown={onSelect ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(template);
        }
      } : undefined}
      // Native tooltip: the only remaining way to see a token's name on
      // hover, now that the hotkey hint (which doubled as a name label) is
      // gone — the picker doesn't have per-item actions to hint at anyway.
      title={template.title}
      aria-label={onSelect ? template.title : undefined}
      style={{
        // Fluid items ride their `1fr` cell (square, capped at `size`); fixed
        // items are exactly `size`. The circular bg/overlay are %-based, so both
        // scale with whatever box this resolves to.
        ...(fluid
          ? { width: '100%', maxWidth: size, aspectRatio: '1 / 1' }
          : { width: size, height: size }),
        cursor: onSelect ? 'pointer' : 'grab',
        userSelect: 'none',
        position: 'relative',
      }}
    >
      {/* circular background */}
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        backgroundColor: template.backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        {template.imageUrl && (
          <img
            src={template.imageUrl}
            alt={template.title}
            className="svg-black"
            style={{ width: '70%', height: '70%', objectFit: 'contain', pointerEvents: 'none' }}
            draggable={false}
          />
        )}
      </div>

      {/* count overlay */}
      {template.count !== undefined && (
        <div style={{
          position: 'absolute',
          top: '-15%',
          left: 0,
          fontSize: 20,
          fontWeight: 'bold',
          color: 'white',
          textShadow: '-2px -2px 0 black, 2px -2px 0 black, -2px 2px 0 black, 2px 2px 0 black',
          pointerEvents: 'none',
          userSelect: 'none',
        }}>
          {template.count}
        </div>
      )}
    </div>
  );
}

interface KeywordTokenGridProps {
  templates: KeywordTokenTemplate[];
  columns?: number;
  rows?: number;
  gap?: number;
  /** Per-item size in px. Fixed mode: the exact size. Fluid mode: the max size
   *  (items shrink below it to fit). Defaults to 40. */
  size?: number;
  /** Fluid: columns are `1fr` and items shrink to fit, so a fixed column count
   *  (e.g. 7) stays on one row per group on narrow screens. Default fixed-px. */
  fluid?: boolean;
  onDragStart?: (template: KeywordTokenTemplate) => void;
  /** When set, items are tap-to-create (touch) instead of drag-only. */
  onSelect?: (template: KeywordTokenTemplate) => void;
}

export const KeywordTokenGrid: React.FC<KeywordTokenGridProps> = ({
  templates,
  columns = 7,
  rows,
  gap = 12,
  size = TOKEN_SIZE,
  fluid = false,
  onDragStart,
  onSelect,
}) => {
  const cell = fluid ? 'minmax(0, 1fr)' : `${size}px`;
  const gridTemplateRows = rows ? `repeat(${rows}, ${fluid ? 'auto' : `${size}px`})` : undefined;

  return (
    <div style={{
      display: 'grid',
      width: fluid ? '100%' : undefined,
      gridTemplateColumns: `repeat(${columns}, ${cell})`,
      gridTemplateRows,
      gap: `${gap}px`,
      padding: '8px',
    }}>
      {templates.map((template) => (
        <TokenGridItem
          key={template.title}
          template={template}
          size={size}
          fluid={fluid}
          onDragStart={onDragStart}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};
